import {ActionMenu, ActionList, Button, LinkButton} from '@primer/react'
import {CopilotIcon, PlusIcon, TriangleDownIcon} from '@primer/octicons-react'
import {FileCheckIcon} from '@github-ui/pacer/CustomIcon'
import {useSubAgents} from '../../hooks/useSubAgents'

import styles from './SubAgentSelector.module.css'
import type {DropdownRepository} from '../../hooks/useCopilotRepoFetching'
import {useMemo} from 'react'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {IconButtonSelector} from '@github-ui/copilot-chat/components/IconButtonSelector'

type SubAgentSelectorProps = {
  repo: DropdownRepository
  branch: string
  selectedAgent: string | null
  onSelect: (agent: string | null) => void
  iconOnly?: boolean
  disabled?: boolean
}

export function SubAgentSelector({
  repo,
  branch,
  selectedAgent,
  onSelect,
  iconOnly = true,
  disabled = false,
}: SubAgentSelectorProps) {
  const {data: agents = [], isLoading} = useSubAgents({repo})

  const selectedAgentObj = agents.find(agent => agent.name === selectedAgent)
  const selectedAgentName = selectedAgentObj?.display_name || selectedAgentObj?.name

  const newFileUrl = useMemo(() => {
    const params = new URLSearchParams({
      filename: '.github/agents/my-agent.md',
      value: '---\nname:\ndescription:\n---\n\n# My Agent\n\nDescribe what your agent does here...',
    })
    return `/${repo.owner}/${repo.nameOnly}/new/${branch}?${params.toString()}`
  }, [repo, branch])

  const triggerLabel = selectedAgentName || 'None'
  const ariaLabel = selectedAgentName ? `Custom agent: ${selectedAgentName}` : 'Select a custom agent'

  return (
    <ActionMenu>
      <ActionMenu.Anchor>
        {iconOnly ? (
          <IconButtonSelector
            icon={CopilotIcon}
            selectedIcon={FileCheckIcon}
            aria-label={ariaLabel}
            selected={Boolean(selectedAgentObj)}
            disabled={disabled || isLoading}
          />
        ) : (
          <Button
            leadingVisual={selectedAgentName ? FileCheckIcon : CopilotIcon}
            trailingAction={TriangleDownIcon}
            aria-label={ariaLabel}
            className={styles.anchor}
            data-selected={Boolean(selectedAgentObj)}
            disabled={disabled || isLoading}
          >
            {triggerLabel}
          </Button>
        )}
      </ActionMenu.Anchor>
      <ActionMenu.Overlay width="auto">
        {agents.length === 0 ? (
          <EmptyState newFileUrl={newFileUrl} />
        ) : (
          <ActionList>
            <ActionList.Group selectionVariant="single">
              <ActionList.Item selected={!selectedAgent} onSelect={() => onSelect(null)}>
                None
              </ActionList.Item>
              {agents.map(agent => {
                const hasConfigError = !!agent.config_error && agent.config_error.length > 0
                return (
                  <ActionList.Item
                    key={agent.name}
                    disabled={hasConfigError}
                    inactiveText={hasConfigError ? agent.config_error : undefined}
                    selected={selectedAgent === agent.name}
                    onSelect={() => onSelect(agent.name)}
                  >
                    {agent.display_name || agent.name}
                  </ActionList.Item>
                )
              })}
            </ActionList.Group>

            <ActionList.Divider />

            <ActionList.Group selectionVariant={false}>
              <ActionList.LinkItem href={newFileUrl} target="_blank">
                <ActionList.LeadingVisual>
                  <PlusIcon />
                </ActionList.LeadingVisual>
                Create an agent
              </ActionList.LinkItem>
            </ActionList.Group>
          </ActionList>
        )}
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

const EmptyState = ({newFileUrl}: {newFileUrl: string}) => (
  <div className={styles.emptyStateContainer}>
    <h3 className={styles.emptyStateTitle}>This repository has no custom agents</h3>
    <p className={styles.emptyStateDescription}>
      Custom agents are reusable instructions and tools in your repository.
    </p>
    <LinkButton href={newFileUrl}>Create a custom agent</LinkButton>
  </div>
)

try{ SubAgentSelector.displayName ||= 'SubAgentSelector' } catch {}
try{ EmptyState.displayName ||= 'EmptyState' } catch {}